// Deep-import: the AI barrel eagerly evaluates the Mastra client + streamdown/parse5/shiki
// stack (~600 KB). AppHeader mounts on every authenticated page, so pulling the barrel here
// would inflate the entry chunk regardless of whether the user opens the assistant.

import { useAIAssistant } from '@cobliteam/ai/src/store/useAIAssistant';
import AIButton from '@cobliteam/ai/src/ui/AIButton';
import { Analytics } from '@cobliteam/analytics';
import type { SessionUser } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import GlobalSearch from '@cobliteam/global-search';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { lazy } from '@cobliteam/lazy-with-retries';
// Deep-import: the barrel would tree-shake fine on its own, but the sibling surfaces
// (Drawer/Toast/FloatingCard) that share the barrel pull @vidstack transitively — deep
// paths remove all doubt about what evaluates when.
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import AppUserButton from '@cobliteam/ui-business/components/AppUser';
import { memo, Suspense } from 'react';
import { SidebarTrigger } from '../AppSidebar';
import FilterGroupComponent from './components/FilterGroupComponent';
import LogoComponent from './components/LogoComponent';
import NotificationsButton from './components/Notifications/NotificationsButton';

// Feedback wraps a Typeform Widget (~58 KB). AppHeader mounts on every page and Feedback
// renders behind a Dialog that only opens on user click. Lazy so Typeform is fetched only
// after the megaphone button is pressed.
const Feedback = lazy(() => import('@cobliteam/ui-business/components/Feedback'));

interface AppHeaderProps {
  user: SessionUser;
  logout: () => void;
}

const AppHeaderAIButton = ({ isAIRouter }: { isAIRouter: boolean }) => {
  const { isAISidepanelOpen, closeAISidepanel, openAISidepanel } = useAIAssistant();

  return (
    <AIButton
      onClick={() => {
        isAISidepanelOpen ? closeAISidepanel() : openAISidepanel();
      }}
      showTooltip
      isSelected={isAISidepanelOpen}
      dataTestId='app-header-ai-button'
      size='sm'
      disabled={isAIRouter}
      variant={isAISidepanelOpen ? 'solid' : 'soft'}
    />
  );
};

const AppHeader = ({ user, logout }: AppHeaderProps) => {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const { runtimeFlags, sidebar } = useDashboard();
  const featureFlags = useFeatureFlags();
  const isAIRouter = sidebar.route?.id === 'ai';
  // Frota inativa: as APIs por trás desses controles ficam bloqueadas (403), então filtro de
  // grupos, busca global, Cobli IA e notificações somem em vez de renderizar quebrados.
  const isInactiveFleet = runtimeFlags.isInactiveFleet;

  return (
    <header
      data-testid='app-header'
      className={cn(
        'sticky top-0 z-50 grid h-14 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 border-gray-6 border-b bg-white px-2 pl-4 shadow-sm transition-shadow duration-200'
      )}
    >
      <div className='relative z-10 flex min-w-0 shrink-0 items-center justify-start gap-1.5 md:gap-2'>
        <SidebarTrigger className='mr-2 -ml-1 desktop:hidden rounded-lg border border-gray-5' />
        <LogoComponent />
        {!isInactiveFleet && <FilterGroupComponent />}
      </div>

      <div className='min-w-0 px-1'>
        <div className='flex min-w-0 justify-end min-[961px]:mx-auto min-[961px]:justify-center'>
          {!(runtimeFlags.noValidGroupOrVehicles || isInactiveFleet) && <GlobalSearch />}
        </div>
      </div>

      <div className='relative z-10 ml-auto flex shrink-0 items-center justify-end gap-2.5'>
        {!isDemoMode && featureFlags.aiChat && !isInactiveFleet && <AppHeaderAIButton isAIRouter={isAIRouter} />}
        <Suspense fallback={null}>
          <Feedback source='navbar'>
            <Button
              data-testid='app-header-feedback-button'
              variant='outlined'
              color='default'
              size='sm'
              icon={<Icon name='megaphone' />}
              className='group'
              onClick={() => Analytics.track('NavBar - Feedback - Click')}
            />
          </Feedback>
        </Suspense>
        {!isInactiveFleet && <NotificationsButton />}
        <AppUserButton user={user} logout={logout} />
      </div>
    </header>
  );
};

export default memo(AppHeader, (prevProps, nextProps) => {
  return prevProps.user.id === nextProps.user.id;
});
